import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnDestroy,
  Type,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { first, switchMap } from "rxjs/operators";
import { ComponentCanDeactivate } from "src/app/shared/Models/interfaces/component-can-deactivate.interface";
import { ToastService } from "src/app/shared/toast.service";
import { SurveyStatus } from "../../../shared/Models/enums/SurveySurveyStatus";
import { InsideSurvey, Survey } from "../../../shared/Models/read";
import { InsideSurveyService } from "../../../shared/services/inside-survey.service";
import { OutsideSurveyService } from "../../../shared/services/outside-survey.service";
import { InsideSurveyComponent } from "../inside-survey/components/inside-survey/inside-survey.component";
import { TSAType } from "../inside-survey/models/tsaType.enum";
import { OutsideSurveyComponent } from "../outside-survey/outside-survey.component";
import { SessionService } from "../shared/services/session.service";
import { ViewOrchestratorDirective } from "../view-orchestrator/view-orchestrator.component";

@Component({
  selector: "app-survey-ochestrator",
  templateUrl: "./survey-ochestrator.component.html",
  styleUrls: ["./survey-ochestrator.component.scss"],
})
export class SurveyOchestratorComponent
  implements AfterViewInit, ComponentCanDeactivate, OnDestroy
{
  @ViewChild(ViewOrchestratorDirective)
  surveyViewContainer: ViewOrchestratorDirective;
  surveyStatusValue: SurveyStatus;
  componentRef: ComponentRef<any>;
  @Input() isPrefill: boolean;
  @Input() isPending: string;
  getSurvey: (id: string) => Observable<InsideSurvey | Survey>;
  getSurveyPhotos: (survey: InsideSurvey | Survey) => void;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private route: ActivatedRoute,
    private router: Router,
    readonly outsideService: OutsideSurveyService,
    readonly insideService: InsideSurveyService,
    private toastService: ToastService,
    protected sessionService: SessionService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  canDeactivate(): boolean {
    if (!this.componentRef?.instance) return true;
    return (<any>this.componentRef.instance).canDeactivate();
  }

  deactivate() {
    if (this.componentRef?.instance?.deactivate)
      this.componentRef.instance.deactivate();
  }

  ngAfterViewInit() {
    this.route.paramMap.pipe(first()).subscribe((data) => {
      this.surveyStatus = +data.get("surveyStatus");
    });
  }

  @Input()
  set surveyStatus(value: SurveyStatus) {
    if (value == null) {
      console.warn(
        "list-item-orchestrator received null as type. Make sure to provide [surveyStatus] for the orchestrator to draw a component."
      );
      return;
    }

    this.surveyStatusValue = value;
    this.loadComponent();
  }

  private loadComponent() {
    this.surveyViewContainer.viewContainerRef.clear();
    this.orchestrate();
  }

  private orchestrate() {
    switch (this.surveyStatusValue) {
      case SurveyStatus.OutsideDone:
      case SurveyStatus.OutsideOnGoing:
      case SurveyStatus.OutsideWaitingForValidation:
      case SurveyStatus.OutsidePending:
        this.getSurvey = (id: string) => this.outsideService.getSurveyInfo(id);
        this.route.paramMap.pipe(first()).subscribe((data) => {
          this.isPrefill = data.get("isPrefill") as unknown as boolean;
        });
        this.getSurveyPhotos = (survey: Survey) => {
          if (survey.building) this.sessionService.setLocation(survey.building);
          survey.photos = this.outsideService.refactorSurveyPhotos(
            survey.photos
          );
        };
        return this.createComponent(OutsideSurveyComponent);
      case SurveyStatus.Prefill:
        this.getSurvey = (id: string) => this.outsideService.getSurveyInfo(id);
        this.route.paramMap.pipe(first()).subscribe((data) => {
          this.isPrefill = data.get("isPrefill") as unknown as boolean;
        });
        this.getSurveyPhotos = (survey: Survey) => {
          if (survey.building) this.sessionService.setLocation(survey.building);
          survey.photos = this.outsideService.refactorSurveyPhotos(
            survey.photos
          );
        };
        return this.createComponent(OutsideSurveyComponent);
      case SurveyStatus.InsideDone:
      case SurveyStatus.InsideOnGoing:
      case SurveyStatus.InsidePending:
      case SurveyStatus.InsideWaitingForValidation:
        this.getSurvey = (id: string) => this.insideService.getSurveyInfo(id);
        this.getSurveyPhotos = (survey: InsideSurvey) => {
          survey.photos = this.insideService.refactorSurveyPhotos(
            survey.photos
          );
          if (survey.buildings?.[0])
            this.sessionService.setLocation(survey.buildings[0]);
          survey.buildings.map(
            (building) =>
              (building.outsideSurvey.photos =
                this.outsideService.refactorSurveyPhotos(
                  building.outsideSurvey.photos
                ))
          );
          if (survey.tsaType == undefined) survey.tsaType = TSAType.NoAdmin;
        };
        return this.createComponent(InsideSurveyComponent);
      default:
        console.error("Couldn't resolve component for survey orchestrator!");
    }
  }

  private createComponent<T>(type: Type<T>) {
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          return this.getSurvey(params.get("id"));
        })
      )
      .pipe(first())
      .subscribe(
        (data: Survey | InsideSurvey) => {
          this.getSurveyPhotos(data);
          this.componentRef =
            this.surveyViewContainer.viewContainerRef.createComponent(
              this.componentFactoryResolver.resolveComponentFactory(type)
            );
          (<{ survey: InsideSurvey | Survey }>(
            (<unknown>this.componentRef.instance)
          )).survey = data;

          (<{ isPrefill: boolean }>(
            (<unknown>this.componentRef.instance)
          )).isPrefill = this.isPrefill;
        },
        (error) => {
          this.toastService.showWarnToast(
            "Survey could not be loaded",
            error.error.message
          );

          this.router.navigateByUrl("/building-table");
        }
      );
  }
}
